import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  FormLayout,
  TextField,
  Select,
  Checkbox,
  Text,
  BlockStack,
  InlineStack,
  Box,
  Divider,
  Button,
  Collapsible,
  Icon,
} from '@shopify/polaris';
import { ChevronDownIcon, ChevronUpIcon } from '@shopify/polaris-icons';
import { WidgetCustomization, defaultWidgetCustomization } from '../types';

interface WidgetCustomizerProps {
  customization: WidgetCustomization;
  onChange: (customization: WidgetCustomization) => void;
  widgetTitle: string;
  widgetStyle: 'grid' | 'carousel' | 'list';
}

// Color picker component using native input
function ColorPicker({ 
  label, 
  value, 
  onChange 
}: { 
  label: string; 
  value: string; 
  onChange: (value: string) => void;
}) {
  return (
    <div style={{ marginBottom: '8px' }}>
      <Text as="span" variant="bodyMd">{label}</Text>
      <InlineStack gap="200" align="start" blockAlign="center">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: '40px',
            height: '32px',
            padding: '2px',
            border: '1px solid #e5e5e5',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        />
        <TextField
          label=""
          labelHidden
          value={value}
          onChange={onChange}
          autoComplete="off"
          monospaced
        />
      </InlineStack>
    </div>
  );
}

// Sample products for preview
const sampleProducts = [
  {
    productId: 1,
    title: 'Classic Running Shoes',
    price: 89.99,
    compareAtPrice: 119.99,
    imageUrl: 'https://placehold.co/300x300/f0f0f0/999?text=Shoes',
  },
  {
    productId: 2,
    title: 'Premium Yoga Mat',
    price: 49.99,
    imageUrl: 'https://placehold.co/300x300/f0f0f0/999?text=Mat',
  },
  {
    productId: 3,
    title: 'Wireless Earbuds Pro',
    price: 159.99,
    compareAtPrice: 199.99,
    imageUrl: 'https://placehold.co/300x300/f0f0f0/999?text=Earbuds',
  },
  {
    productId: 4,
    title: 'Stainless Steel Water Bottle',
    price: 29.99,
    imageUrl: 'https://placehold.co/300x300/f0f0f0/999?text=Bottle',
  },
];

export function WidgetCustomizer({
  customization,
  onChange,
  widgetTitle,
  widgetStyle,
}: WidgetCustomizerProps) {
  const { t } = useTranslation();
  const [openSection, setOpenSection] = useState<string | null>('colors');

  const updateField = <K extends keyof WidgetCustomization>(
    field: K,
    value: WidgetCustomization[K]
  ) => {
    onChange({ ...customization, [field]: value });
  };

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  const resetToDefaults = () => {
    onChange(defaultWidgetCustomization);
  };

  // Generate CSS for preview
  const previewStyles = `
    .preview-widget {
      font-family: ${customization.fontFamily === 'inherit' ? "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" : customization.fontFamily};
      padding: 20px;
      background: ${customization.backgroundColor};
    }
    .preview-title {
      font-size: ${customization.titleFontSize};
      font-weight: 600;
      margin-bottom: 16px;
      color: ${customization.textColor};
    }
    .preview-grid {
      display: ${widgetStyle === 'carousel' ? 'flex' : 'grid'};
      ${widgetStyle === 'carousel' ? 'overflow-x: auto; scroll-snap-type: x mandatory;' : ''}
      ${widgetStyle === 'grid' ? `grid-template-columns: repeat(auto-fill, minmax(${customization.cardMinWidth}, 1fr));` : ''}
      ${widgetStyle === 'list' ? 'grid-template-columns: 1fr;' : ''}
      gap: ${customization.gridGap};
    }
    .preview-card {
      background: ${customization.backgroundColor};
      border: 1px solid ${customization.cardBorderColor};
      border-radius: ${customization.borderRadius};
      overflow: hidden;
      ${customization.cardShadow ? 'box-shadow: 0 2px 8px rgba(0,0,0,0.1);' : ''}
      ${widgetStyle === 'carousel' ? `flex: 0 0 ${customization.cardMinWidth}; scroll-snap-align: start;` : ''}
      ${widgetStyle === 'list' ? 'display: flex; align-items: center;' : ''}
    }
    .preview-image {
      width: ${widgetStyle === 'list' ? '80px' : '100%'};
      aspect-ratio: ${customization.imageAspectRatio};
      object-fit: ${customization.imageObjectFit};
      background: #f5f5f5;
    }
    .preview-content {
      padding: ${customization.cardPadding};
      ${widgetStyle === 'list' ? 'flex: 1;' : ''}
    }
    .preview-badge {
      display: ${customization.showBadge ? 'inline-block' : 'none'};
      font-size: 0.75rem;
      padding: 2px 8px;
      border-radius: 4px;
      margin-bottom: 8px;
      background: ${customization.badgeBackgroundColor};
      color: ${customization.badgeTextColor};
    }
    .preview-product-title {
      font-size: ${customization.productTitleFontSize};
      font-weight: 500;
      margin: 0 0 8px 0;
      color: ${customization.textColor};
      line-height: 1.3;
    }
    .preview-price {
      font-size: ${customization.priceFontSize};
      font-weight: 600;
      color: ${customization.priceColor};
    }
    .preview-compare-price {
      font-size: 0.85rem;
      color: #999;
      text-decoration: line-through;
      margin-left: 8px;
    }
  `;

  const SectionHeader = ({ title, section }: { title: string; section: string }) => (
    <div
      onClick={() => toggleSection(section)}
      style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
    >
      <Text as="h3" variant="headingSm">{title}</Text>
      <Icon source={openSection === section ? ChevronUpIcon : ChevronDownIcon} />
    </div>
  );

  return (
    <BlockStack gap="500">
      {/* Preview Section */}
      <Card>
        <Box padding="400">
          <BlockStack gap="400">
            <InlineStack align="space-between">
              <Text as="h2" variant="headingMd">{t('widgetCustomizer.widgetPreview')}</Text>
              <Button variant="plain" onClick={resetToDefaults}>{t('widgetCustomizer.resetToDefaults')}</Button>
            </InlineStack>
            <Box
              background="bg-surface-secondary"
              padding="400"
              borderRadius="200"
              borderWidth="025"
              borderColor="border"
            >
              <style>{previewStyles}</style>
              <div className="preview-widget">
                <h3 className="preview-title">{widgetTitle}</h3>
                <div className="preview-grid">
                  {sampleProducts.slice(0, widgetStyle === 'list' ? 2 : 4).map((product, index) => (
                    <div key={product.productId} className="preview-card">
                      <img
                        className="preview-image"
                        src={product.imageUrl}
                        alt={product.title}
                      />
                      <div className="preview-content">
                        {index === 0 && <span className="preview-badge">{customization.badgeText}</span>}
                        <h4 className="preview-product-title">{product.title}</h4>
                        <div>
                          <span className="preview-price">${product.price.toFixed(2)}</span>
                          {product.compareAtPrice && (
                            <span className="preview-compare-price">${product.compareAtPrice.toFixed(2)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Box>
          </BlockStack>
        </Box>
      </Card>

      {/* Customization Options */}
      <Card>
        <Box padding="400">
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">{t('widgetCustomizer.widgetCustomization')}</Text>
            
            {/* Colors Section */}
            <Box paddingBlockStart="200">
              <SectionHeader title={t('widgetCustomizer.colors')} section="colors" />
              <Collapsible open={openSection === 'colors'} id="colors-section">
                <Box paddingBlockStart="400">
                  <BlockStack gap="400">
                    <InlineStack gap="800" wrap>
                      <ColorPicker
                        label={t('widgetCustomizer.primaryColor')}
                        value={customization.primaryColor}
                        onChange={(value) => updateField('primaryColor', value)}
                      />
                      <ColorPicker
                        label={t('widgetCustomizer.backgroundColor')}
                        value={customization.backgroundColor}
                        onChange={(value) => updateField('backgroundColor', value)}
                      />
                    </InlineStack>
                    <InlineStack gap="800" wrap>
                      <ColorPicker
                        label={t('widgetCustomizer.textColor')}
                        value={customization.textColor}
                        onChange={(value) => updateField('textColor', value)}
                      />
                      <ColorPicker
                        label={t('widgetCustomizer.priceColor')}
                        value={customization.priceColor}
                        onChange={(value) => updateField('priceColor', value)}
                      />
                    </InlineStack>
                    <ColorPicker
                      label={t('widgetCustomizer.cardBorderColor')}
                      value={customization.cardBorderColor}
                      onChange={(value) => updateField('cardBorderColor', value)}
                    />
                  </BlockStack>
                </Box>
              </Collapsible>
            </Box>

            <Divider />

            {/* Badge Section */}
            <Box paddingBlockStart="200">
              <SectionHeader title={t('widgetCustomizer.badge')} section="badge" />
              <Collapsible open={openSection === 'badge'} id="badge-section">
                <Box paddingBlockStart="400">
                  <FormLayout>
                    <Checkbox
                      label={t('widgetCustomizer.showBadge')}
                      checked={customization.showBadge}
                      onChange={(checked) => updateField('showBadge', checked)}
                      helpText={t('widgetCustomizer.showBadgeHelp')}
                    />
                    {customization.showBadge && (
                      <BlockStack gap="400">
                        <TextField
                          label={t('widgetCustomizer.badgeText')}
                          value={customization.badgeText}
                          onChange={(value) => updateField('badgeText', value)}
                          autoComplete="off"
                        />
                        <InlineStack gap="800" wrap>
                          <ColorPicker
                            label={t('widgetCustomizer.badgeBackground')}
                            value={customization.badgeBackgroundColor}
                            onChange={(value) => updateField('badgeBackgroundColor', value)}
                          />
                          <ColorPicker
                            label={t('widgetCustomizer.badgeTextColor')}
                            value={customization.badgeTextColor}
                            onChange={(value) => updateField('badgeTextColor', value)}
                          />
                        </InlineStack>
                      </BlockStack>
                    )}
                  </FormLayout>
                </Box>
              </Collapsible>
            </Box>

            <Divider />

            {/* Typography Section */}
            <Box paddingBlockStart="200">
              <SectionHeader title={t('widgetCustomizer.typography')} section="typography" />
              <Collapsible open={openSection === 'typography'} id="typography-section">
                <Box paddingBlockStart="400">
                  <FormLayout>
                    <Select
                      label={t('widgetCustomizer.fontFamily')}
                      options={[
                        { label: t('widgetCustomizer.fontInherit'), value: 'inherit' },
                        { label: t('widgetCustomizer.fontSystemUI'), value: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
                        { label: t('widgetCustomizer.fontGeorgia'), value: 'Georgia, serif' },
                        { label: t('widgetCustomizer.fontArial'), value: 'Arial, sans-serif' },
                        { label: t('widgetCustomizer.fontHelvetica'), value: 'Helvetica, Arial, sans-serif' },
                        { label: t('widgetCustomizer.fontVerdana'), value: 'Verdana, sans-serif' },
                      ]}
                      value={customization.fontFamily}
                      onChange={(value) => updateField('fontFamily', value)}
                    />
                    <FormLayout.Group>
                      <Select
                        label={t('widgetCustomizer.sectionTitleSize')}
                        options={[
                          { label: `${t('widgetCustomizer.sizeSmall')} (1.25rem)`, value: '1.25rem' },
                          { label: `${t('widgetCustomizer.sizeMedium')} (1.5rem)`, value: '1.5rem' },
                          { label: `${t('widgetCustomizer.sizeLarge')} (1.75rem)`, value: '1.75rem' },
                          { label: `${t('widgetCustomizer.sizeExtraLarge')} (2rem)`, value: '2rem' },
                        ]}
                        value={customization.titleFontSize}
                        onChange={(value) => updateField('titleFontSize', value)}
                      />
                      <Select
                        label={t('widgetCustomizer.productTitleSize')}
                        options={[
                          { label: `${t('widgetCustomizer.sizeSmall')} (0.8rem)`, value: '0.8rem' },
                          { label: `${t('widgetCustomizer.sizeMedium')} (0.9rem)`, value: '0.9rem' },
                          { label: `${t('widgetCustomizer.sizeLarge')} (1rem)`, value: '1rem' },
                          { label: `${t('widgetCustomizer.sizeExtraLarge')} (1.1rem)`, value: '1.1rem' },
                        ]}
                        value={customization.productTitleFontSize}
                        onChange={(value) => updateField('productTitleFontSize', value)}
                      />
                    </FormLayout.Group>
                    <Select
                      label={t('widgetCustomizer.priceSize')}
                      options={[
                        { label: `${t('widgetCustomizer.sizeSmall')} (0.9rem)`, value: '0.9rem' },
                        { label: `${t('widgetCustomizer.sizeMedium')} (1rem)`, value: '1rem' },
                        { label: `${t('widgetCustomizer.sizeLarge')} (1.1rem)`, value: '1.1rem' },
                        { label: `${t('widgetCustomizer.sizeExtraLarge')} (1.25rem)`, value: '1.25rem' },
                      ]}
                      value={customization.priceFontSize}
                      onChange={(value) => updateField('priceFontSize', value)}
                    />
                  </FormLayout>
                </Box>
              </Collapsible>
            </Box>

            <Divider />

            {/* Layout & Spacing Section */}
            <Box paddingBlockStart="200">
              <SectionHeader title={t('widgetCustomizer.layout')} section="layout" />
              <Collapsible open={openSection === 'layout'} id="layout-section">
                <Box paddingBlockStart="400">
                  <FormLayout>
                    <FormLayout.Group>
                      <Select
                        label={t('widgetCustomizer.borderRadius')}
                        options={[
                          { label: t('widgetCustomizer.radiusNone'), value: '0' },
                          { label: t('widgetCustomizer.radiusSmall'), value: '4px' },
                          { label: t('widgetCustomizer.radiusMedium'), value: '8px' },
                          { label: t('widgetCustomizer.radiusLarge'), value: '12px' },
                          { label: t('widgetCustomizer.radiusExtraLarge'), value: '16px' },
                          { label: t('widgetCustomizer.radiusRounded'), value: '24px' },
                        ]}
                        value={customization.borderRadius}
                        onChange={(value) => updateField('borderRadius', value)}
                      />
                      <Select
                        label={t('widgetCustomizer.gridGap')}
                        options={[
                          { label: t('widgetCustomizer.gapTight'), value: '12px' },
                          { label: t('widgetCustomizer.gapNormal'), value: '20px' },
                          { label: t('widgetCustomizer.gapLoose'), value: '28px' },
                          { label: t('widgetCustomizer.gapExtraLoose'), value: '36px' },
                        ]}
                        value={customization.gridGap}
                        onChange={(value) => updateField('gridGap', value)}
                      />
                    </FormLayout.Group>
                    <FormLayout.Group>
                      <Select
                        label={t('widgetCustomizer.cardMinWidth')}
                        options={[
                          { label: t('widgetCustomizer.widthCompact'), value: '150px' },
                          { label: t('widgetCustomizer.widthNormal'), value: '200px' },
                          { label: t('widgetCustomizer.widthWide'), value: '250px' },
                          { label: t('widgetCustomizer.widthExtraWide'), value: '300px' },
                        ]}
                        value={customization.cardMinWidth}
                        onChange={(value) => updateField('cardMinWidth', value)}
                      />
                      <Select
                        label={t('widgetCustomizer.cardPadding')}
                        options={[
                          { label: t('widgetCustomizer.paddingCompact'), value: '8px' },
                          { label: t('widgetCustomizer.paddingNormal'), value: '12px' },
                          { label: t('widgetCustomizer.paddingComfortable'), value: '16px' },
                          { label: t('widgetCustomizer.paddingSpacious'), value: '20px' },
                        ]}
                        value={customization.cardPadding}
                        onChange={(value) => updateField('cardPadding', value)}
                      />
                    </FormLayout.Group>
                    <Checkbox
                      label={t('widgetCustomizer.showCardShadow')}
                      checked={customization.cardShadow}
                      onChange={(checked) => updateField('cardShadow', checked)}
                      helpText={t('widgetCustomizer.showCardShadowHelp')}
                    />
                  </FormLayout>
                </Box>
              </Collapsible>
            </Box>

            <Divider />

            {/* Image Section */}
            <Box paddingBlockStart="200">
              <SectionHeader title={t('widgetCustomizer.images')} section="images" />
              <Collapsible open={openSection === 'images'} id="images-section">
                <Box paddingBlockStart="400">
                  <FormLayout>
                    <FormLayout.Group>
                      <Select
                        label={t('widgetCustomizer.imageAspectRatio')}
                        options={[
                          { label: t('widgetCustomizer.ratioSquare'), value: '1' },
                          { label: t('widgetCustomizer.ratioPortrait'), value: '3/4' },
                          { label: t('widgetCustomizer.ratioLandscape'), value: '4/3' },
                          { label: t('widgetCustomizer.ratioWide'), value: '16/9' },
                        ]}
                        value={customization.imageAspectRatio}
                        onChange={(value) => updateField('imageAspectRatio', value)}
                      />
                      <Select
                        label={t('widgetCustomizer.imageFit')}
                        options={[
                          { label: t('widgetCustomizer.fitCover'), value: 'cover' },
                          { label: t('widgetCustomizer.fitContain'), value: 'contain' },
                          { label: t('widgetCustomizer.fitFill'), value: 'fill' },
                        ]}
                        value={customization.imageObjectFit}
                        onChange={(value) => updateField('imageObjectFit', value as 'cover' | 'contain' | 'fill')}
                      />
                    </FormLayout.Group>
                  </FormLayout>
                </Box>
              </Collapsible>
            </Box>
          </BlockStack>
        </Box>
      </Card>
    </BlockStack>
  );
}
